import { useRef } from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowLeft, ArrowRight, Check, Clock, IndianRupee, Users } from "lucide-react";
import { PageShell } from "@/components/site/PageShell";
import { Reveal, SectionLabel } from "@/components/site/Reveal";
import { ZONES } from "@/data/content";
import { ZONE_DETAILS } from "@/data/zoneDetails";
import { useGoTo } from "@/lib/useGoTo";

const ease = [0.22, 1, 0.36, 1];

const ZoneHero = ({ z }) => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "20%"]);
  return (
    <section ref={ref} className="relative h-[78svh] min-h-[520px] overflow-hidden" data-testid="zone-hero">
      <motion.img key={z.key} style={{ y, scale: 1.15 }} initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 1 }} src={z.img} alt={z.title} className="absolute inset-0 w-full h-full object-cover" />
      <div className="absolute inset-0 bg-gradient-to-t from-[#07080E] via-[#07080E]/50 to-[#07080E]/30" />
      <div className="relative h-full max-w-[1400px] mx-auto px-5 md:px-10 flex flex-col justify-end pb-14">
        <Link to="/" state={{ scrollTo: "zones" }} data-testid="zone-back-link" className="inline-flex items-center gap-2 text-sm text-slate-200 hover:text-[#FFEF00] mb-6 w-fit"><ArrowLeft size={16} /> All zones</Link>
        <p className="font-mono text-xs uppercase tracking-[0.3em] text-[#FFEF00] mb-4">{z.tag}</p>
        <h1 className="font-display font-black uppercase leading-[0.95] text-[2.6rem] leading-[1] sm:text-6xl lg:text-8xl break-words" data-testid="zone-title">
          {z.title.split(" ").map((w, i) => (
            <span key={`${z.key}-${i}`} className="inline-block overflow-hidden mr-4 pb-2 -mb-2 align-bottom">
              <motion.span className="inline-block" initial={{ y: "110%" }} animate={{ y: 0 }} transition={{ duration: 1, delay: 0.2 + i * 0.12, ease }}>{w}</motion.span>
            </span>
          ))}
        </h1>
      </div>
    </section>
  );
};

const InfoCard = ({ z, d }) => {
  const go = useGoTo();
  const rows = [[Clock, "Timings", "[Opening hours]"], [IndianRupee, "Price", "[Price per person]"], [Users, "Good for", d.goodFor]];
  return (
    <div className="rounded-[28px] bg-[#FFEF00] text-black border-[3px] border-black shadow-[10px_10px_0_#14C3F7] p-7 lg:sticky lg:top-28" data-testid="zone-info-card">
      <p className="font-display font-extrabold text-2xl">{z.title}</p>
      <div className="mt-5 divide-y divide-black/15">
        {rows.map(([Icon, k, v]) => (
          <div key={k} className="flex items-start gap-3 py-3">
            <Icon size={18} className="mt-0.5 shrink-0" />
            <div><p className="font-mono text-[11px] uppercase tracking-[0.2em] text-black/60">{k}</p><p className="font-semibold">{v}</p></div>
          </div>
        ))}
      </div>
      <button data-testid="zone-book-btn" onClick={() => go("book")} className="mt-6 w-full rounded-full bg-black text-[#FFEF00] py-4 font-display font-bold hover:bg-[#0E101D] transition-colors">Book now</button>
      <button data-testid="zone-enquire-btn" onClick={() => go("enquire")} className="mt-3 w-full rounded-full border-2 border-black py-3.5 font-semibold hover:bg-black/5 transition-colors">Group / party enquiry</button>
    </div>
  );
};

const MoreZones = ({ current }) => {
  const idx = ZONES.findIndex((x) => x.key === current);
  const more = [1, 2, 3, 4].map((o) => ZONES[(idx + o) % ZONES.length]);
  return (
    <section className="py-20 border-t border-white/10" data-testid="more-zones">
      <div className="max-w-[1400px] mx-auto px-5 md:px-10">
        <div className="flex items-end justify-between mb-10">
          <h2 className="font-display font-black uppercase text-3xl md:text-4xl">Keep playing</h2>
          <Link to={`/zones/${more[0].key}`} data-testid="zone-next-link" className="hidden sm:inline-flex items-center gap-2 text-sm hover:text-[#FFEF00]">Next: {more[0].title} <ArrowRight size={16} /></Link>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {more.map((m) => (
            <Link key={m.key} to={`/zones/${m.key}`} data-testid={`more-zone-${m.key}`} className="group relative block aspect-[4/5] overflow-hidden rounded-[24px] border border-white/10 hover:border-[#14C3F7]/70 transition-colors">
              <img src={m.img} alt={m.title} loading="lazy" className="absolute inset-0 w-full h-full object-cover transition-transform duration-[1.2s] group-hover:scale-110" />
              <div className="absolute inset-0 bg-gradient-to-t from-[#07080E] to-transparent" />
              <p className="absolute bottom-5 left-5 right-5 font-display font-extrabold text-xl">{m.title}</p>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
};

export default function ZonePage() {
  const { key } = useParams();
  const z = ZONES.find((x) => x.key === key);
  const d = ZONE_DETAILS[key];
  if (!z || !d) return <Navigate to="/" replace />;
  return (
    <PageShell testId="zone-page">
      <ZoneHero z={z} />
      <section className="py-16 md:py-24">
        <div className="max-w-[1400px] mx-auto px-5 md:px-10 grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-7">
            <Reveal>
              <SectionLabel>About this zone</SectionLabel>
              <p className="font-display font-semibold text-2xl md:text-3xl leading-snug" data-testid="zone-intro">{d.intro}</p>
            </Reveal>
            <Reveal delay={0.1}>
              <ul className="mt-12 space-y-4">
                {d.highlights.map((h, i) => (
                  <li key={h} className="flex items-center gap-4 rounded-2xl border border-white/10 bg-[#0E101D] px-5 py-4" data-testid={`zone-highlight-${i}`}>
                    <span className="w-9 h-9 rounded-full bg-[#14C3F7] text-black flex items-center justify-center shrink-0"><Check size={18} /></span>
                    <span className="text-base md:text-lg">{h}</span>
                  </li>
                ))}
              </ul>
            </Reveal>
          </div>
          <div className="lg:col-span-5"><InfoCard z={z} d={d} /></div>
        </div>
      </section>
      <MoreZones current={key} />
    </PageShell>
  );
}
